import RoleService from "@/services/api/role";
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from "@reduxjs/toolkit";

interface RoleState {
  roles: any[];
  isFetching: boolean;
  error: string | null;
}

// 👇 Async thunk
export const getRoles = createAsyncThunk(
  "role/getRoles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await RoleService.getRoles();
      return response.data || [];
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch roles");
    }
  }
);

export const deleteRole = createAsyncThunk(
  "role/deleteRole",
  async (roleId: number, { rejectWithValue }) => {
    try {
      const response = await RoleService.deleteRole(roleId);
      return response.data || [];
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to delete role");
    }
  }
);

export const addRole = createAsyncThunk(
  "role/addRole",
  async (role: any, { rejectWithValue }) => {
    try {
      const response = await RoleService.addRole(role);
      return response.data || [];
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to add role");
    }
  }
);

export const updateRole = createAsyncThunk(
  "role/updateRole",
  async (role: any, { rejectWithValue }) => {
    try {
      const response = await RoleService.updateRole(role);
      return response.data || [];
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to update role");
    }
  }
);

export const getPermissions = createAsyncThunk(
  "role/getPermissions",
  async (roleId: any, { rejectWithValue }) => {
    try {
      const response = await RoleService.getPermissions(roleId);
      return response.data || [];
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch role permissions");
    }
  }
);

export const updatePermission = createAsyncThunk(
  "role/updatePermission",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await RoleService.updatePermission(data);
      return response.data || [];
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to update permissions");
    }
  }
);

export const getAuditLogsById = createAsyncThunk(
  "role/getAuditLogsById",
  async (roleId: any, { rejectWithValue }) => {
    try {
      const response = await RoleService.getAuditLogsById(roleId);
      return response.data || [];
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch audit details");
    }
  }
);

const initialState: RoleState = {
  roles: [],
  isFetching: false,
  error: null,
};

const roleSlice = createSlice({
  name: "role",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // specific updates
    builder.addCase(getRoles.fulfilled, (state, action) => { state.roles = action.payload; });
    builder.addCase(addRole.fulfilled, (state, action) => { state.roles.push(action.payload); });
    builder.addCase(updateRole.fulfilled, (state, action) => { 
      const matchedIndex = state.roles.findIndex(r => r.Id === action.payload.Id);
      if (matchedIndex !== -1) state.roles[matchedIndex] = action.payload;
    });
    builder.addCase(deleteRole.fulfilled, (state, action) => { 
      state.roles = state.roles.filter(r => r.Id !== action.payload); 
    });
    builder.addCase(getPermissions.fulfilled, (state, action) => { state.roles = action.payload; });
     
    // Generic loading & error
    builder.addMatcher(isPending, (state) => { state.isFetching = true; state.error = null; });
    builder.addMatcher(isRejected, (state, action) => { state.isFetching = false; state.error = action.payload as string; });
    builder.addMatcher(isFulfilled, (state) => { state.isFetching = false; });
  },
});

export default roleSlice.reducer;
